// @flow

import { useDispatch } from 'react-redux';

import {
  getCart,
  selectCartOwnerList,
  selectCartProductOwner,
  selectCartUUID,
  selectReselleeIdOfCartProducts,
  setCartOwnerIsReseller,
  setCartProductOwner,
  setCartReselleeId,
  updateCart,
  type ProductPayload,
} from '@gandi/cart';
import {
  selectCurrentId,
  selectPersonalId,
  setCurrentId,
} from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';

import { updateProductsPayload } from 'src/Cart/AccountChoice/modules/actions';
import { fetchCustomersList } from 'src/Cart/Customer/modules/customers';
import type { CartSummaryContextType } from 'src/Layout/context/CartSummary';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { postCartCheckout } from 'src/modules/app/actions';
import { unsetReselleeFromConfiguration } from 'src/modules/configurations';

type Props = {
  onSuccess: () => void,
  updateSharingIdContext: CartSummaryContextType['updateSharingIdContext'],
};

export function useApplyInvoicedOrga({
  onSuccess,
  updateSharingIdContext,
}: Props): (newOwnerId: string) => Promise<void> {
  const dispatch = useDispatch();
  const cartOwnerList = useAppSelector(selectCartOwnerList);
  const reselleeId = useAppSelector(selectReselleeIdOfCartProducts);
  const productOwnerId = useAppSelector(selectCartProductOwner);
  const cartId = useAppSelector(selectCartUUID);
  const sharingId = useAppSelector(selectCurrentId);
  const personalId = useAppSelector(selectPersonalId);
  const translator = useTranslator();

  return async (newInvoicedOrgaId: string) => {
    if (!newInvoicedOrgaId) return;

    // Update cart owner
    try {
      const isReseller = cartOwnerList.some(
        invoiceContact =>
          invoiceContact.id === newInvoicedOrgaId && invoiceContact.reseller,
      );

      dispatch(setCartOwnerIsReseller(isReseller));

      const shouldUnsetProductOwner =
        reselleeId || newInvoicedOrgaId === productOwnerId;

      // $FlowFixMe[prop-missing] data of updateCart are not ProductPayload but a full cart
      const data: Partial<ProductPayload> = {
        owner_uuid: newInvoicedOrgaId,
        has_orga: true,
      };

      if (shouldUnsetProductOwner) {
        dispatch(setCartProductOwner(null));
        data.product_owner_uuid = null;
      }

      // patch the cart with the new ownership
      await dispatch(
        updateCart(cartId, data, {
          headers: { 'X-Dry-Run': '0' },
        }),
      );

      // patch the product payload if needed
      if (reselleeId) {
        dispatch(setCartReselleeId(null));
        await Promise.all([
          // TODO: Check if the payload is really unset
          dispatch(updateProductsPayload({ translator })),
          dispatch(unsetReselleeFromConfiguration()),
        ]);
      }

      // ask for reprice, non-blocking
      dispatch(postCartCheckout()).then(() => dispatch(getCart()));

      // Change ownerId in the URL without reloading the page
      const currentUrl = window.location.pathname;
      const newUrl = currentUrl.replace(sharingId, newInvoicedOrgaId);
      window.history.replaceState(null, '', newUrl);

      await dispatch(getCart()).then(() =>
        dispatch(setCurrentId(newInvoicedOrgaId)),
      );

      // Call after getCart to ensure that the component doesn't re-render before closing.
      if (isReseller) {
        dispatch(fetchCustomersList(newInvoicedOrgaId));
      }

      // XXX: move here some logic to fetch candidates
      // (organization which eligible to be productOwner behind the new invoiced one)

      updateSharingIdContext({
        newSharingId: newInvoicedOrgaId,
        guess: newInvoicedOrgaId === personalId,
      });

      onSuccess();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      // pass
    }
  };
}
